import { computed, DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';

import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import * as LiveUpdates from '@capacitor/live-updates';
import { Preferences } from '@capacitor/preferences';

const RELOAD_BUILD_KEY = 'liveUpdateLastReloadBuildId';
const RELOAD_TIME_KEY = 'liveUpdateLastReloadAttemptAt';
const RELOAD_GUARD_MS = 10 * 60 * 1000;

export type LiveUpdateState = 'idle' | 'checking' | 'upToDate' | 'updateReady' | 'error';

@Injectable({ providedIn: 'root' })
export class LiveUpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  readonly isNative = Capacitor.isNativePlatform();
  readonly supported = this.isNative && Capacitor.isPluginAvailable('LiveUpdates');
  private readonly currentState = signal<LiveUpdateState>('idle');
  readonly state = this.currentState.asReadonly();
  private readonly latestResult = signal<LiveUpdates.SyncResult | null>(null);
  readonly result = this.latestResult.asReadonly();
  readonly buildId = computed(() => this.result()?.snapshot?.buildId);
  readonly channel = computed(() => this.result()?.liveUpdate.channel);
  readonly appId = computed(() => this.result()?.liveUpdate.appId);
  private readonly errorMessage = signal<string | null>(null);
  readonly error = this.errorMessage.asReadonly();
  private readonly reloading = signal(false);
  readonly isReloading = this.reloading.asReadonly();
  readonly updateReady = computed(() => this.state() === 'updateReady');
  readonly statusMessage = computed(() => ({
    idle: 'Updates download in the background.',
    checking: 'Checking for app updates...',
    upToDate: "You're up to date",
    updateReady: 'Update ready',
    error: 'Could not check for updates. You can continue using the app and try again later.',
  })[this.state()]);
  private started = false;
  private pendingUpdate = false;
  private inFlight?: Promise<void>;
  private resumeListener?: PluginListenerHandle;

  constructor() {
    this.destroyRef.onDestroy(() => { void this.resumeListener?.remove(); });
  }

  /** Called once after platform readiness; never waits for a download. */
  start(): void {
    if (!this.supported || this.started) return;
    this.started = true;
    void this.listenForResume(() => {
      this.zone.run(() => { void this.checkForUpdate(); });
    }).then(handle => {
      if (this.destroyRef.destroyed) void handle.remove();
      else this.resumeListener = handle;
    }).catch(error => console.warn('Live update resume listener unavailable', error));
    void this.checkForUpdate();
  }

  /** All callers share one JavaScript-initiated sync; the SDK also auto-downloads natively. */
  checkForUpdate(): Promise<void> {
    if (!this.supported || this.reloading()) return Promise.resolve();
    if (this.inFlight) return this.inFlight;
    if (this.pendingUpdate) return Promise.resolve();
    this.currentState.set('checking');
    this.errorMessage.set(null);
    // Defer SDK invocation until inFlight is assigned, including synchronous failures.
    this.inFlight = Promise.resolve().then(() => this.sync()).then(async result => {
      this.latestResult.set(result);
      if (result.activeApplicationPathChanged === true) {
        const [build, timestamp] = await Promise.all([
          this.readPreference({ key: RELOAD_BUILD_KEY }),
          this.readPreference({ key: RELOAD_TIME_KEY }),
        ]);
        const attemptedAt = Number(timestamp.value);
        const age = Date.now() - attemptedAt;
        if (result.snapshot?.buildId && build.value === result.snapshot.buildId &&
            timestamp.value !== null && Number.isFinite(attemptedAt) && age >= 0 && age < RELOAD_GUARD_MS) {
          console.warn('Suspected Live Updates reload loop; suppressing prompt temporarily', {
            buildId: result.snapshot.buildId, attemptedAt, channel: result.liveUpdate.channel,
          });
          this.errorMessage.set('Update application deferred. You can continue using the app.');
          this.currentState.set('error');
          return;
        }
        this.pendingUpdate = true;
      }
      this.currentState.set(this.pendingUpdate ? 'updateReady' : 'upToDate');
    }).catch(error => {
      console.warn('Live update check failed; continuing with the running bundle', error);
      this.errorMessage.set('Could not check for updates. Please try again later.');
      this.currentState.set(this.pendingUpdate ? 'updateReady' : 'error');
    }).finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  /** Only invoked by the Update now action; checks and lifecycle events never reload. */
  async reloadToUpdate(): Promise<void> {
    if (!this.supported || !this.pendingUpdate || this.reloading()) return;
    this.reloading.set(true);
    this.errorMessage.set(null);
    try {
      // Store the timestamp last: a partial write must never describe a completed attempt.
      await this.writePreference({ key: RELOAD_BUILD_KEY, value: this.buildId() ?? '' });
      await this.writePreference({ key: RELOAD_TIME_KEY, value: String(Date.now()) });
      await this.reload();
      // Keep ready state until this WebView is replaced by the new bundle.
    } catch (error) {
      console.warn('Live update reload failed', error);
      this.errorMessage.set('Could not apply the update. Please try Update now again.');
    } finally {
      this.reloading.set(false);
    }
  }

  protected readPreference(options: { key: string }): Promise<{ value: string | null }> {
    return Preferences.get(options);
  }

  protected writePreference(options: { key: string; value: string }): Promise<void> {
    return Preferences.set(options);
  }

  protected listenForResume(listener: () => void): Promise<PluginListenerHandle> {
    return App.addListener('resume', listener);
  }

  protected sync(): Promise<LiveUpdates.SyncResult> {
    return LiveUpdates.sync();
  }

  protected reload(): Promise<void> {
    return LiveUpdates.reload();
  }
}
